<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text class="editor-content">
        <section class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8"><v-text-field v-model="fields.title" label="Name" hide-details/></v-col>
            <v-col cols="12" md="4"
              ><v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details
            /></v-col>
          </v-row>
        </section>

        <SectionSettingsContainerFuild
          class="mb-4"
          :settings="fields.settings"
          :container-page="fields.containerPage"
          :show-container-page="false"
          :use-background-mobile="false"
          :show-background-image="false"
        />

        <section class="mb-4">
          <label class="label-input">Header</label>
          <div class="group-item">
            <p class="field-label">Title</p>
            <ckeditor :editor="editor" v-model="fields.header.title" :config="editorConfig" />
            <p class="field-label mt-4">Description</p>
            <ckeditor :editor="editor" v-model="fields.header.description" :config="editorConfig" />
          </div>
        </section>

        <section class="mb-4">
          <label class="label-input">Media</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4"><v-checkbox v-model="fields.media.show" label="Show media" hide-details/></v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="fields.media.position"
                  :items="positionOptions"
                  label="Desktop position"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="fields.media.type" :items="mediaTypeOptions" label="Media type" hide-details />
              </v-col>
            </v-row>
            <template v-if="fields.media.type === 'video'">
              <v-text-field class="mt-4" v-model="fields.media.videoSrc" label="Video URL" hide-details />
              <video
                v-if="fields.media.videoSrc"
                class="media-preview"
                :src="fields.media.videoSrc"
                controls
                muted
                playsinline
              />
            </template>
            <template v-else>
              <p class="field-label mt-4">Image</p>
              <UploadImage
                :image="{ imageSrc: fields.media.imageSrc }"
                :height="'220'"
                :typeUpload="'image'"
                @callback="handleUploadMediaImage"
                @callbackDelete="handleRemoveMediaImage"
              />
            </template>
          </div>
        </section>

        <section>
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Steps</label>
            <v-btn small text color="primary" @click="handleAddStep"><v-icon small>mdi-plus</v-icon>Add step</v-btn>
          </div>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(item, index) in fields.steps" :key="`furnitureshop-step-${index}`">
              <v-expansion-panel-header>
                <span>Step {{ index + 1 }}</span>
                <template #actions
                  ><v-btn icon @click.stop="handleRemoveStep(index)"
                    ><v-icon color="error">mdi-delete</v-icon></v-btn
                  ></template
                >
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="field-label">Image</p>
                <UploadImage
                  :image="{ imageSrc: item.image }"
                  :height="'180'"
                  :typeUpload="'image'"
                  @callback="obj => handleUploadStepImage(obj, index)"
                  @callbackDelete="() => handleRemoveStepImage(index)"
                />
                <p class="field-label mt-4">Description</p>
                <ckeditor :editor="editor" v-model="item.description" :config="editorConfig" />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </section>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions
          ><v-spacer /><v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn
          ><v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn></v-card-actions
        >
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const stepImage = 'https://cdn.skyfastimg.com/images/2026/08/20/image-6a86742b4fd03c8374fd33c3.webp?w=1000';
const defaultStep = () => ({
  image: stepImage,
  description:
    '<h3 style="text-align: center">TITLE&nbsp;</h3> <p style="text-align: center">Description</p> <p style="text-align: center">&nbsp;</p> <h2 style="text-align: center"> <span style="color: hsl(25, 96%, 47%)"><strong>Value</strong></span> </h2> <p style="text-align: center">Description</p>',
});

export default {
  name: 'FurnitureshopCardStep',
  components: { UploadImage, SectionSettingsContainerFuild },
  props: { sectionEditor: { type: Object, required: true } },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      isUploadingVideo: false,
      positionOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Right', value: 'right' },
      ],
      mediaTypeOptions: [
        { text: 'Video', value: 'video' },
        { text: 'Image', value: 'image' },
      ],
      fields: {
        title: 'Furnitureshop card step',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#2A2A2A',
          textColor: '#FFFFFF',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
        header: {
          title: '<h2>Mirror Finish,<br>Instantly Visible</h2>',
          description:
            '<p>Fills micro-imperfections in the clear coat<br>to create a flawless, mirror-smooth surface.</p>',
        },
        media: { show: true, position: 'left', type: 'video', videoSrc: '', imageSrc: '' },
        steps: [defaultStep(), defaultStep(), defaultStep()],
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(value) {
        if (value?.sectionExtract) this.applyExtract(value.sectionExtract);
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        header: { ...this.fields.header, ...(extract.header || {}) },
        media: { ...this.fields.media, ...(extract.media || {}) },
        steps: Array.isArray(extract.steps) && extract.steps.length ? extract.steps : this.fields.steps,
      };
    },
    generateObjectID() {
      const hex = value => Math.floor(value).toString(16);
      return hex(Date.now() / 1000) + ' '.repeat(16).replace(/./g, () => hex(Math.random() * 16));
    },
    handleUploadMediaImage(obj) {
      this.fields.media.imageSrc = obj?.imageSrc || '';
    },
    handleRemoveMediaImage() {
      this.fields.media.imageSrc = '';
    },
    handleAddStep() {
      this.fields.steps.push(defaultStep());
    },
    handleRemoveStep(index) {
      this.fields.steps.splice(index, 1);
    },
    handleUploadStepImage(obj, index) {
      if (this.fields.steps[index]) this.$set(this.fields.steps[index], 'image', obj?.imageSrc || '');
    },
    handleRemoveStepImage(index) {
      if (this.fields.steps[index]) this.$set(this.fields.steps[index], 'image', '');
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionFurnitureshopCardStep(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', { ...this.sectionEditor.section, title: this.fields.title, html });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.media-preview {
  display: block;
  width: 100%;
  max-height: 260px;
  margin-top: 16px;
  background: #000;
}
::v-deep .ck-editor__editable_inline {
  min-height: 100px;
}
</style>
